import {buildStandardHeaders, OtsAsyncThunkConfigExtra, TransportEntity} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import {BaseQueryApi} from "@reduxjs/toolkit/query";
import {useDebouncedState} from "@mantine/hooks";

export type UseDownloadTransportPdfProps = {
  baseQueryApi: BaseQueryApi
}

export type UseDownloadTransportPdfResult = {
  downloadTransportPdf: (transport: TransportEntity, onSuccess: (() => void) | undefined) => void
}

export const useDownloadTransportPdf = (props: UseDownloadTransportPdfProps): UseDownloadTransportPdfResult => {
  const {baseQueryApi} = props

  const {t} = useTranslation(["common"])

  const [_downloading, _setDownloading] = useDebouncedState(false, 500)

  useEffect(() => {
    _setDownloading(_downloading)
  }, [_downloading, _setDownloading])

  useEffect(() => {
    if (_downloading) {
      notifications.show({
        id: "useDownloadTransportPdf_updating",
        title: t("common.status.downloading", "Downloading") + " " + t("common.entity.transport", "Transport"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDownloadTransportPdf_updating")
    }
  }, [_downloading, t]);

  const _handleError = useCallback((rejected: any) => {
    notifications.show({
      color: "red",
      title: t("common.error", "Error"),
      message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
    })
  }, [t])

  const _downloadTransportPdf = useCallback((transport: TransportEntity, onSuccess: (() => void) | undefined) => {
    _setDownloading(true)

    const headers = buildStandardHeaders(baseQueryApi)
    headers["Accept"] = 'application/text'
    headers["Content-Type"] = 'application/text'

    const request: RequestInit = {
      method: 'GET',
      headers: headers,
    }

    const baseUrl = (baseQueryApi.extra as OtsAsyncThunkConfigExtra).baseUrl;
    const url = new URL(`${baseUrl}/transport/${transport.id}/pdf`);

    // url.searchParams.append("bytes", "true")
    if (transport.dbName) {
      url.searchParams.append('dbName', transport.dbName);
    }

    window.fetch(url, request)
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorDetail) => {
            return Promise.reject(errorDetail);
          });
        }
        return response.text()
      })
      .then((text) => {
        window.open(text, '_blank');

        notifications.show({
          color: "green",
          title: t("common.entity.transport", "Transport") + " " + t("common.status.downloaded", "Downloaded"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((error) => {
        _handleError(error)
      })
      .finally(() => {
        _setDownloading(false);
      });

  }, [_handleError, _setDownloading, baseQueryApi, t])

  return {
    downloadTransportPdf: _downloadTransportPdf,
  }
}
